/*
    Note: The timeouts set are to simulate the fetch requests that will be added
    in the future once the API is set up.
*/

export class Investment {
    constructor(element) {
        this.element = element;
        this.table = this.element.querySelector(".js-investment-table");
        this.investments = this.element.querySelectorAll(".js-investment-item");
        this.checkedSVG;
        this.addSVG;
        this.minusSVG;

        this.init();
    }

    init() {
        this.addInvestmentItemListener();
    }

    addInvestmentItemListener() {
        this.investments.forEach((item) => {
            item.querySelector(".cta-button").addEventListener("click", (event) => {
                event.preventDefault();

                if (this.isLoading([...this.investments])) {
                    throw new Error("Already loading an item");
                }

                const id = item.getAttribute("data-id");

                if (this.isAdded(id)) {
                    this.handleItemRemove(event, item);
                } else {
                    this.handleItemAdd(event, item);
                }
            });
        });
    }

    async handleItemRemove(event, item) {
        item.classList.add("loading");

        const button = event.currentTarget;
        this.setButtonLabel(button, "Removing...");

        const id = item.getAttribute("data-id");
        try {
            const rowID = await this.removeItemFromTable(id);
            if (!rowID) {
                throw new Error("Failed to remove item from table");
            }

            setTimeout(() => {
                item.classList.remove("loading");
                item.classList.remove("added");

                this.setButtonSVG(button, this.addSVG);
                this.setButtonLabel(button, "Add");
                button.querySelector('button').blur();
            }, 2000);
        } catch (error) {
            console.error("Error removing item from table:", error);
            this.setButtonLabel(button, "Added");
            item.classList.remove("loading");
        }
    }

    async handleItemAdd(event, item) {
        item.classList.add("loading");

        const button = event.currentTarget;
        this.setButtonLabel(button, "Adding...");

        const id = item.getAttribute("data-id");
        try {
            const rowID = await this.addItemToTable(id);
            if (!rowID) {
                throw new Error("Failed to add item to table");
            }

            const rowElement = this.table.querySelector(".investment__row[data-id='" + rowID + "']");
            setTimeout(() => {
                this.scrollTo(rowElement);
            }, 1000);

            setTimeout(() => {
                item.classList.remove("loading");
                item.classList.add("added");

                this.setButtonSVG(button, this.checkedSVG);
                button.querySelector(".cta-button__svg:not(.cta-button__svg--shadow)").innerHTML = this.minusSVG;

                this.setButtonLabel(button, "Added");
                button.querySelector('button').blur();

                rowElement.classList.remove("highlight");
            }, 2000);
        } catch (error) {
            console.error("Error adding item to table:", error);
            this.setButtonLabel(button, "Add");
            item.classList.remove("loading");
        }
    }

    async removeItemFromTable(id) {
        return new Promise((resolve, reject) => {
            const row = this.table.querySelector(".investment__row[data-id='" + id + "']");
            if (!row) {
                return reject(new Error("Row not found for id: " + id));
            }

            row.style.transform = "translateX(-100%)";
            row.style.height = "0";

            setTimeout(() => {
                row.remove();
            }, 1000);

            resolve(id);
        });
    }

    async addItemToTable(id) {
        return new Promise((resolve, reject) => {
            // Simulate a fetch request to get the data from the server
            // For now, we will just create a mock data object
            const data = {
                id: id,
                feature: "Feature " + id,
                design: "Design " + id,
                development: "Development " + id,
                cost: "$" + (id * 1000)
            };

            if (!data) {
                return reject(new Error("Failed to fetch data for id: " + id));
            }

            const row = this.tableRowTemplate(data);
            if (!row) {
                return reject(new Error("Failed to create table row for id: " + id));
            }

            this.table.insertAdjacentHTML("beforeend", row);
            resolve(id);
        });
    }

    /*
        Helper Functions
    */
    isLoading(items) {
        const isLoading = items.map((element) => element.classList.contains("loading"));
        if (isLoading.some((loading) => loading)) {
            return true;
        }

        return false;
    }

    isAdded(id) {
        const isAdded = [...this.table.querySelectorAll(".investment__row")].map((element) => element.getAttribute("data-id"));
        if (isAdded.includes(id)) {
            return true;
        }

        return false;
    }

    setButtonLabel(button, text) {
        if (!button || !text) {
            console.error("Button or text is not defined");
            return;
        }

        const buttonLabel = button.querySelector(".cta-button__label");
        buttonLabel.textContent = text;
    }

    setButtonSVG(button, svg) {
        const buttonIcons = button.querySelectorAll(".cta-button__svg");
        buttonIcons.forEach((icon) => {
            icon.innerHTML = svg;
        });
    }

    scrollTo(element) {
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest"
            });
        }
    }

    /*
        Templates
    */
    tableRowTemplate(data) {
        const { feature, design, development, cost } = data;
        return `
             <div class="investment__row highlight" data-id="${data.id}">
                <div class="investment__column investment__column--feature">
                    ${feature}
                </div>
                <div class="investment__column investment__column--design">
                    ${design}
                </div>
                <div class="investment__column investment__column--development">
                    ${development}
                </div>
                <div class="investment__column investment__column--cost">
                    ${cost}
                </div>
            </div>
        `;
    }

    get checkedSVG() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 -960 960 960">
                <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z"/>
            </svg>
        `;
    }

    get addSVG() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="currentColor">
                <path d="M444-444H276q-15.3 0-25.65-10.29Q240-464.58 240-479.79t10.35-25.71Q260.7-516 276-516h168v-168q0-15.3 10.29-25.65Q464.58-720 479.79-720t25.71 10.35Q516-699.3 516-684v168h168q15.3 0 25.65 10.29Q720-495.42 720-480.21t-10.35 25.71Q699.3-444 684-444H516v168q0 15.3-10.29 25.65Q495.42-240 480.21-240t-25.71-10.35Q444-260.7 444-276v-168Z"/>
            </svg>
        `;
    }

    get minusSVG() {
        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 -960 960 960">
                <path d="M200-440v-80h560v80H200Z"/>
            </svg>
        `;
    }
}